// src/components/CompanyDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { companyAPI } from "../services/api";
import "./CompanyDetails.css";
import CompanyCharts from "./CompanyCharts";
import { motion } from "framer-motion";
import DisclaimerPopup from "./DisclaimerPopup";
import ErrorPage from "../components/ErrorPage";
import "./CompanyDetails.css";



const CompanyDetails = () => {
  const { symbol } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shareTab, setShareTab] = useState("quarterly");
  const [lastClosePrice, setLastClosePrice] = useState(null);
  const [lastCloseDate, setLastCloseDate] = useState(null);
  const [currentPriceTimestamp, setCurrentPriceTimestamp] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;

  const mainHeader = document.querySelector("header"); // your main site header
  const subNav = document.querySelector(".cd-top-nav");

  const mainHeaderHeight = mainHeader?.offsetHeight || 0;
  const subNavHeight = subNav?.offsetHeight || 0;

  const totalOffset = mainHeaderHeight + subNavHeight + 12;

  const y =
    el.getBoundingClientRect().top + window.scrollY - totalOffset;

  window.scrollTo({ top: y, behavior: "smooth" });
};

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const data = await companyAPI.getCompanyDetails(symbol);

        if (!data) {
          setError("No company data found.");
          return;
        }

        setCompany(data);
        
      //   const peerList = await companyAPI.getPeers(symbol);
      // setPeers(peerList);
  } catch (err) {
  let errorMessage = "Something went wrong. Please try again.";

  // ✅ IMPORTANT: capture HTTP status
  setErrorStatus(err.status || err.response?.status || null);

  // If API returned a JSON error (PascalCase)
  if (err?.response?.data?.Message) {
    errorMessage = err.response.data.Message;
  }
  // If backend sends lowercase message
  else if (err?.response?.data?.message) {
    errorMessage = err.response.data.message;
  }
  // If custom thrown Error (your API layer)
  else if (err?.message) {
    errorMessage = err.message;
  }

  setError(errorMessage);
}

    finally {
      setLoading(false);
    }
  };
  
    fetchCompany();
  }, [symbol]);

  useEffect(() => {
    const fetchLastClose = async () => {
      if (!symbol) return;
      try {
        const response = await companyAPI.getHistorical(symbol);
        let candles = response;
        if (Array.isArray(response)) {
          candles = response;
        } else if (response?.data && Array.isArray(response.data)) {
          candles = response.data;
        } else if (response?.historicals && Array.isArray(response.historicals)) {
          candles = response.historicals;
        }
        const sorted = (candles || [])
          .map((candle) => ({
            price: parseFloat(
              candle?.close ?? candle?.price ?? candle?.Close ?? candle?.Price ?? 0
            ),
            date: candle?.time || candle?.date || candle?.Date || candle?.timestamp
          }))
          .filter((entry) => entry.price && entry.date)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        if (sorted.length) {
          setLastClosePrice(sorted[0].price);
          setLastCloseDate(sorted[0].date);
        }
      } catch (err) {
        console.error("Historical price fetch error:", err);
        setLastClosePrice(null);
        setLastCloseDate(null);
      }
    };
    fetchLastClose();
  }, [symbol]);

  const renderTable = (matrix) => {
    if (!matrix || matrix.length === 0) {
      return <p className="cd-no-data">No data available.</p>;
    }

    const headers = matrix[0];
    const rows = matrix.slice(1);

    return (
      <div className="cd-table-wrapper">
        <table className="cd-table"
      style={{ "--cols": headers.length }}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "cd-row-striped" : ""}>
                {row.map((c, ci) => (
                  <td key={ci}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
//   const generatePeersTable = () => {
//   if (!peers || peers.length === 0)
//     return <p className="cd-no-data">No peer data available.</p>;

//   const headers = [
//     "Company",
//     "CMP Rs.",
//     "P/E",
//     "Mar Cap Rs.Cr.",
//     "Div Yld %",
//     "NP Qtr Rs.Cr.",
//     "Qtr Profit Var %",
//     "Sales Qtr Rs.Cr.",
//     "Qtr Sales Var %",
//     "ROCE %",
//   ];

//   const rows = peers.map((p) => [
//     p.company_name || p.name,
//     p.cmp || "-",
//      p.pe || "-",
//     p.marketCap || "-",
//     p.dividendYield || "-",
//     p.netProfitQuarter|| "_",
//     p.qtrProfitVar || "-",
//     p.salesQuarter || "-",
//     p.qtrSalesVar || "_",
//     p.roce || "-",
//   ]);

//   return renderTable([headers, ...rows]);
// };


  const name = company?.company || company?.name || company?.CompanyName || "";
  const tradingSymbol = company?.trading_Symbol || company?.trading_symbol || "";
  const data = company?.data || {};
  const ratios = data.top_ratios || {};

  const marketCap = ratios["Market Cap"];
  const currentPrice = ratios["Current Price"];
  const currentNumericPrice = useMemo(() => {
    if (!currentPrice) return null;
    const sanitized = parseFloat(currentPrice.replace(/[^\d.]/g, ""));
    return Number.isNaN(sanitized) ? null : sanitized;
  }, [currentPrice]);

  useEffect(() => {
    if (currentPrice) {
      setCurrentPriceTimestamp(new Date());
    }
  }, [currentPrice]);

  const priceSummary = useMemo(() => {
    if (!currentNumericPrice || !lastClosePrice) return null;
    const diff = currentNumericPrice - lastClosePrice;
    const percent = lastClosePrice ? (diff / lastClosePrice) * 100 : 0;
    return {
      direction: diff >= 0 ? "up" : "down",
      formattedPercent: `${diff >= 0 ? "+" : ""}${percent.toFixed(2)}%`,
      formattedPriceDiff: `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}`
    };
  }, [currentNumericPrice, lastClosePrice]);

  const formattedLastUpdate = useMemo(() => {
    if (!currentPriceTimestamp) return null;
    const formatted = currentPriceTimestamp.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
      // hour: "numeric",
      // minute: "2-digit",
      // hour12: true
    });
    return formatted.replace("AM", "a.m.").replace("PM", "p.m.");
  }, [currentPriceTimestamp]);
  const highLow = ratios["High / Low"];
  const stockPE = ratios["Stock P/E"];
  const bookValue = ratios["Book Value"];
  const divYield = ratios["Dividend Yield"];
  const roce = ratios["ROCE"] || ratios["roce"];
  const roe = ratios["ROE"] || ratios["roe"];
  const faceValue = ratios["Face Value"];

 // Loading
// if (loading) {
//   return (
//     <div className="state-wrapper">
//       <div className="loading-card">
//         <div className="spinner"></div>
//         <p>Loading company details…</p>
//       </div>
//     </div>
//   );
// }
// Loading
if (loading) {
  return (
    <div className="wf-wrapper">
      <div className="wf-card">
        <div className="wf-header">
          <h1>Loading company information</h1>
        </div>

        <div className="wf-body">
          <p className="wf-desc">
            Please wait while we retrieve the latest company details.
          </p>

          <div className="wf-spinner"></div>
        </div>
      </div>
    </div>
  );
}

// Error
// if (error) {
//   return (
//     <div className="state-wrapper">
//       <div className="error-card">
//         <div className="error-icon">⚠</div>
//         <p>{error}</p>
//         <button className="retry-btn" onClick={() => window.location.reload()}>
//           Retry
//         </button>
//       </div>
//     </div>
//   );
// }
// Error
if (error) {
  return (
     <ErrorPage
      error={error}
      errorStatus={errorStatus}
      onRetry={() => window.location.reload()}
    />
  );
}

  
  

  return (
    <>
   <DisclaimerPopup
  message="Financial charts are based on publicly available data and are for informational purposes only."
  intervalMinutes={1}   
  autoCloseSeconds={5}
  position="bottom-right"
/>
    {/* top sticky tab nav */}
      <div className="cd-top-nav">
        {[
          ["overview", name],
          ["chart", "Chart"],
          // ["peers", "Peers"],
          ["quarters", "Quarters"],
          ["profit-loss", "Profit & Loss"],
          ["balance-sheet", "Balance Sheet"],
          ["cashflow", "Cash Flows"],
          ["ratios", "Ratios"],
          ["shareholding", "Investors"],
        ].map(([id, label]) => (
          <button
            key={id}
            className="cd-top-nav-item"
            onClick={() => scrollToSection(id)}
          >
            {label}
          </button>
        ))}
      </div>
    <div className="cd-container">
      <div className="cd-page-wrapper">
         {/* ✅ SHOW ERROR HERE */}
    {error && (
      <div className="error-box">
        {error}
      </div>
    )}
        {/* HEADER + RATIOS CARD + ABOUT */}
        <div className="cd-card-container fade-in">
          <div id="overview" className="cd-header-overview">
            {/* main header row */}
            <div className="cd-header">
              <div className="cd-header-left">
                <h1>
                  {name} <span className="cd-symbol">({tradingSymbol})</span>
                </h1>
              </div>

              <div className="cd-header-price">
                <div className="cd-price-badge">
                  <div className="cd-price-main">
                    {currentPrice || "-"}
                  </div>
                  {priceSummary && (
                    <div className="cd-price-sub">
                      <span className={`cd-price-change ${priceSummary.direction}`}>
                        <span className="cd-price-arrow">
                          {priceSummary.direction === "up" ? "▲" : "▼"}
                        </span>
                        {priceSummary.formattedPercent}
                      </span>
                      {formattedLastUpdate && (
                        <span className="cd-price-time">{formattedLastUpdate}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* OVERVIEW GRID – ratios + about */}
            <div className="cd-overview-grid">
              {/* left: ratios card */}
              <div className="cd-overview-left">
                <div className="cd-ratios-card">
                  {/* top row */}
                  <div className="cd-ratios-top-row">
                    {marketCap && (
                      <div className="cd-ratio-block">
                        <div className="label">Market Cap </div>
                        <div className="value">{marketCap}</div>
                     
                      </div>
                    )}
                    {currentPrice && (
                      <div className="cd-ratio-block">
                        <div className="label">Current Price</div>
                        <div className="value">{currentPrice}</div>
                      </div>
                    )}
                    {highLow && (
                      <div className="cd-ratio-block">
                        <div className="label">High / Low</div>
                        <div className="value">{highLow}</div>
                      </div>
                    )}
                  </div>

                  {/* middle row – grey pills */}
                  <div className="cd-ratios-middle-row">
                    {stockPE && (
                      <div className="cd-ratio-pill">
                        <div className="label">Stock P/E</div>
                        <div className="value">{stockPE}</div>
                      </div>
                    )}
                    {bookValue && (
                      <div className="cd-ratio-pill">
                        <div className="label">Book Value</div>
                        <div className="value">{bookValue}</div>
                      </div>
                    )}
                    {divYield && (
                      <div className="cd-ratio-pill">
                        <div className="label">Dividend Yield</div>
                        <div className="value">{divYield}</div>
                      </div>
                    )}
                  </div>

                  {/* bottom row */}
                  <div className="cd-ratios-bottom-row">
                    {roce && (
                      <div className="cd-ratio-inline">
                        <div className="label">ROCE</div>
                        <div className="value">{roce}</div>
                      </div>
                    )}
                    {roe && (
                      <div className="cd-ratio-inline">
                        <div className="label">ROE</div>
                        <div className="value">{roe}</div>
                      </div>
                    )}
                    {faceValue && (
                      <div className="cd-ratio-inline">
                        <div className="label">Face Value</div>
                        <div className="value">{faceValue}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* right: about + key points */}
              <div className="cd-overview-right">
                <div>
                  <h3 className="cd-side-title">ABOUT</h3>
                  <p className="cd-side-text">{data.about}</p>
                </div>

                {/* {data.key_points && (
                  <div>
                    <h3 className="cd-side-title">KEY POINTS</h3>
                    <div className="cd-keypoints-box">
                      {data.key_points.map((kp, idx) => (
                        <p key={idx} className="cd-keypoints-text">
                          {kp}
                        </p>
                      ))}
                      <button className="cd-readmore-btn">
                        READ MORE →
                      </button>
                    </div>
                  </div> */}
                {/* )} */}
              </div>
            </div>
          </div>
        </div>
{/* 
        {/* CHARTS SECTION - Now using the new component */}
        <div className="cd-card-container">
          <section id="chart" className="cd-section">
            <h2>Charts</h2>
            <CompanyCharts companyData={company} symbol={symbol} />
          </section>
        </div> 

        {/* peers */}
{/*     
          <div className="cd-card-container">
            <section id="peers" className="cd-section">
    <h2>Peer Comparison</h2>
    <p id="peerdata" className="cd-section-subtitle">
      Companies in the same sector
    </p> */}

    {/* {generatePeersTable()} */}
      {/* </section>
          </div> */}
    {/* <div className="cd-card-container">
            <section id="peers" className="cd-section">
              <h2>Peer Comparison</h2>
              <p className="cd-section-subtitle">
                (Sector info can be shown here)
              </p>
              {renderTable(data.peers)}
            </section>
          </div>

       */}

        {/* quarterly */}
        <div className="cd-card-container">
          <section id="quarters" className="cd-section">
            <h2>Quarterly Results</h2>
            <p className="cd-section-subtitle">
              Consolidated Figures in Rs. Crores
            </p>
            {renderTable(data.quarterly_results || data.quaterly_results)}
          </section>
        </div>

        {/* profit & loss */}
        <div className="cd-card-container">
          <section id="profit-loss" className="cd-section">
            <h2>Profit &amp; Loss</h2>
            <p className="cd-section-subtitle">
              Consolidated Figures in Rs. Crores
            </p>
            {renderTable(data.profit_and_loss)}
          </section>
        </div>

        {/* balance sheet */}
        <div className="cd-card-container">
          <section id="balance-sheet" className="cd-section">
            <h2>Balance Sheet</h2>
            <p className="cd-section-subtitle">
              Consolidated Figures in Rs. Crores
            </p>
            {renderTable(data.balance_sheet)}
          </section>
        </div>

        {/* cash flows */}
        <div className="cd-card-container">
          <section id="cashflow" className="cd-section">
            <h2>Cash Flows</h2>
            <p className="cd-section-subtitle">
              Consolidated Figures in Rs. Crores
            </p>
            {renderTable(data.cash_flows)}
          </section>
        </div>

        {/* ratios */}
        <div className="cd-card-container">
          <section id="ratios" className="cd-section">
            <h2>Ratios</h2>
            <p className="cd-section-subtitle">
              Consolidated Figures in Rs. Crores
            </p>
            {renderTable(data.ratios)}
          </section>
        </div>

        {/* shareholding */}
        <div className="cd-card-container">
          <section id="shareholding" className="cd-section">
            <div className="cd-share-header-row">
      <h2>Shareholding Pattern</h2>

            <div className="cd-share-toggle">
              <button
                onClick={() => setShareTab("quarterly")}
                className={shareTab === "quarterly" ? "active" : ""}
              >
                Quarterly
              </button>
              <button
                onClick={() => setShareTab("yearly")}
                className={shareTab === "yearly" ? "active" : ""}
              >
                Yearly
              </button>
            </div>
             </div>
<div className="cd-share-table">
      {shareTab === "quarterly"
        ? renderTable(data.shareholding_quarterly)
        : renderTable(data.shareholding_yearly)}
    </div>
             
          </section>
        </div>
      </div>
    </div>
    </>
  );
};

export default CompanyDetails;